import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  DropdownList,
  type DropdownListGroup,
  type IDropdownListItem,
} from "@/components/ui/dropdown-list/dropdown-list";
import { nodeVariableToGroups, type NodeVariable } from "../types";

interface VariableSelectorProps {
  variables: NodeVariable[];
  type: "string" | "number";
  selectedValue?: string;
  onUpdateElement: (value: string) => void;
  placeholder?: string;
  group: "right" | "up-right" | "down-right";
  useNameAsValue?: boolean; // When true, uses variable name as value instead of ID
}

export const VariableSelector: React.FC<VariableSelectorProps> = ({
  variables,
  type,
  selectedValue,
  onUpdateElement,
  placeholder = "Elegir variable",
  group = "right",
  useNameAsValue = false,
}) => {
  const [variableSearch, setVariableSearch] = useState("");
  const [_selectedVariable, setSelectedVariable] =
    useState<IDropdownListItem>();
  const [_selectedGroup, setSelectedGroup] = useState<DropdownListGroup>();

  // Create unfiltered groups for lookup (not affected by search)
  const allVariableGroups = useMemo(() => {
    const groups = nodeVariableToGroups(variables);
    const getAllowedTypes = (type: string) => {
      switch (type) {
        case "string":
          return ["string", "number"];
        case "number":
          return ["number"];
        default:
          return ["string", "number"];
      }
    };
    return groups
      .map((group) => ({
        ...group,
        items: group.items
          .filter((item) => {
            return getAllowedTypes(type).includes(item.detail || "");
          })
          .map((item) => {
            // If useNameAsValue is true, use the label (variable name) as the value
            if (useNameAsValue) {
              return {
                ...item,
                value: item.label, // Use name as value
              };
            }
            return item; // Keep original value (ID)
          }),
      }))
      .filter((group) => group.items.length > 0);
  }, [variables, type, useNameAsValue]);

  // Create filtered groups for display (affected by search)
  const variableGroups = useMemo(() => {
    if (!variableSearch.trim()) {
      return allVariableGroups;
    }

    const searchLower = variableSearch.toLowerCase();
    return allVariableGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          return (
            (item?.label as string)?.toLowerCase()?.includes(searchLower) ||
            group.groupLabels.some((label) =>
              label.toLowerCase().includes(searchLower),
            )
          );
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [allVariableGroups, variableSearch]);

  // Sync local state when selectedValue prop changes
  useEffect(() => {
    if (!selectedValue) {
      // Clear selection if selectedValue is empty
      setSelectedVariable(undefined);
      setSelectedGroup(undefined);
      return;
    }

    // Find the variable in allVariableGroups (unfiltered) so we can always find it
    // even if it's filtered out by search
    // Note: We read allVariableGroups from closure but don't include it in deps
    // to prevent loops. It's memoized so it's stable unless variables/type change.
    let foundVariable: IDropdownListItem | undefined;
    let foundGroup: DropdownListGroup | undefined;

    for (const group of allVariableGroups) {
      const variable = group.items.find((item) => item.value === selectedValue);
      if (variable) {
        foundVariable = variable as IDropdownListItem;
        foundGroup = group as DropdownListGroup;
        break;
      }
    }

    // Only update state if we found a match
    if (foundVariable && foundGroup) {
      // Use functional updates and check if values changed to prevent unnecessary updates
      setSelectedVariable((prev) => {
        if (prev?.value === foundVariable.value) {
          return prev; // No change needed
        }
        return foundVariable;
      });

      // Update group only if it changed
      setSelectedGroup((prev) => {
        if (prev?.groupId === foundGroup.groupId) {
          return prev; // No change needed
        }
        return foundGroup;
      });
    } else {
      // Clear selection if variable not found
      setSelectedVariable((prev) => {
        if (!prev) return prev; // Already cleared
        return undefined;
      });
      setSelectedGroup((prev) => {
        if (!prev) return prev; // Already cleared
        return undefined;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue]); // Only depend on selectedValue to prevent infinite loops

  const onVariableSelection = useCallback(
    (variableId: string, groupId: string) => {
      // Search in allVariableGroups to ensure we can always find the variable
      const group = allVariableGroups.find((g) => g.groupId === groupId);
      const variable = group?.items.find((item) => item.value === variableId);

      if (
        variable &&
        typeof variable.value === "string" &&
        variable.value !== selectedValue
      ) {
        // ONLY update parent if value actually changed
        // Do NOT update local state here - the effect will handle it
        // This prevents double updates and infinite loops
        onUpdateElement(variable.value);
      }
    },
    [allVariableGroups, onUpdateElement, selectedValue],
  );

  // Memoize the toggleSelection callback to prevent recreations
  const handleToggleSelection = useCallback(
    (variable: IDropdownListItem, group?: DropdownListGroup) => {
      onVariableSelection(variable.value, group?.groupId || "");
    },
    [onVariableSelection],
  );

  // Memoize the search callback
  const handleSearch = useCallback((query: string) => {
    setVariableSearch(query);
  }, []);

  return (
    <DropdownList
      group={group}
      options={variableGroups as DropdownListGroup[]}
      selectedValue={_selectedVariable}
      selectedGroup={_selectedGroup}
      toggleSelection={handleToggleSelection}
      widthClassName="flex-1"
      placeholder={placeholder}
      search={{
        placeholder: "Buscar variable",
        onSearch: handleSearch,
        query: variableSearch,
      }}
    />
  );
};
